import React, { memo, useMemo, useState } from 'react';
import cn from 'classnames';

import { IDynamicIconWrapperProps } from './DynamicIconWrapper.types';

import Tooltip from '../Tooltip';

import './DynamicIconWrapper.scss';

const DynamicIconWrapper: React.FC<IDynamicIconWrapperProps> = ({
	icon: Icon,
	isActive,
	isDirty,
	isDisabled,
	isSelected,
	onClick,
	selectable = false,
	tooltipLabel,
	withHover,
	tooltipPlacement = 'auto',
	testName = '',
	iconStyle,
	wrapperStyle,
	...rest
}) => {
	const styles = useMemo(() => {
		return selectable
			? { style: { padding: '2px' } }
			: {};
	}, [selectable]);

	const className = cn('f5gDynamicIconWrapperIcon', {
		'f5gDynamicIconWrapperActive': isActive && !isDisabled && !selectable,
		'f5gDynamicIconWrapperDirty': isDirty,
		'f5gDynamicIconWrapperDisabled': isDisabled && !isActive,
		'f5gDynamicIconWrapperSelectable': selectable,
	});

	const [isActiveIcon, setIsActiveIcon] = useState(false);

	const handleMouseEnter = () => {
		if (withHover) setIsActiveIcon(true);
	};

	const handleMouseLeave = () => {
		if (withHover) setIsActiveIcon(false);
	};

	const iconProps = useMemo(() => {
		return {
			isActive: withHover ? isActiveIcon : isActive,
			isDisabled,
			className,
			...rest,
			...styles,
			innerSvgProps: {
				className,
				...styles,
				...iconStyle,
			},
		};
	}, [className, isActive, isActiveIcon, isDisabled, rest, styles, withHover, iconStyle]);

	const onClickHandler = () => {
		if (isDisabled || !onClick) return;

		onClick();
	};

	return tooltipLabel
		? (
			<Tooltip
				content={tooltipLabel}
				placement={tooltipPlacement}
			>
				<div
					className='f5gDynamicIconWrapperIcon'
					data-testid={`f5g-dynamic-icon-wrapper${testName}`}
					onClick={onClickHandler}
					onMouseOut={handleMouseLeave}
					onMouseOver={handleMouseEnter}
					style={wrapperStyle}
				>
					<Icon {...iconProps} />
				</div>
			</Tooltip>
		)
		: <div
			className='f5gDynamicIconWrapperIcon'
			data-testid={`f5g-dynamic-icon-wrapper${testName}`}
			onClick={onClickHandler}
			onMouseOut={handleMouseLeave}
			onMouseOver={handleMouseEnter}
			style={wrapperStyle}
		>
			<Icon {...iconProps} />
		</div>;
};

export default memo(DynamicIconWrapper);
